/**
 * Clean up speech-recognition transcripts.
 *
 * Browser SpeechRecognition restarts often (especially on Android). Each new session
 * re-transcribes the utterance from the start, so a naive buffer ends up with text like
 * "do you think do you think you would do you think you would have …". These helpers
 * merge overlapping fragments and collapse repeated phrases so both the AI prompt and
 * the saved transcript stay readable.
 */

const MAX_CHAT_TEXT_CHARS = 900;
const MAX_DASHBOARD_TEXT_CHARS = 1400;
const WAKE_PREFIX_RE = /^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(berwind|berwin|bervind)(\s+of\s+the\s+elms)?\s*[,\-:]*\s*/i;

function normalizeSpaces(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function cleanToken(token) {
  return String(token || '').toLowerCase().replace(/^[^\w']+|[^\w']+$/g, '');
}

function samePhrase(tokens, a, b, size) {
  for (let i = 0; i < size; i += 1) {
    if (cleanToken(tokens[a + i]) !== cleanToken(tokens[b + i])) return false;
  }
  return true;
}

/** Collapse immediately repeated phrases ("a b a b a b" → "a b") and stuttered words. */
export function collapseRepeatedPhrases(text) {
  const tokens = normalizeSpaces(text).split(' ').filter(Boolean);
  if (tokens.length <= 3) return normalizeSpaces(text);
  const output = [];
  let i = 0;
  while (i < tokens.length) {
    let matched = false;
    const maxSize = Math.min(16, Math.floor((tokens.length - i) / 2));
    for (let size = maxSize; size >= 2; size -= 1) {
      if (!samePhrase(tokens, i, i + size, size)) continue;
      output.push(...tokens.slice(i, i + size));
      i += size;
      while (i + size <= tokens.length && samePhrase(tokens, i - size, i, size)) i += size;
      matched = true;
      break;
    }
    if (matched) continue;
    const token = tokens[i];
    const prev = output[output.length - 1];
    const prev2 = output[output.length - 2];
    if (cleanToken(token) === cleanToken(prev) && cleanToken(token) === cleanToken(prev2)) {
      i += 1;
      continue;
    }
    output.push(token);
    i += 1;
  }
  return normalizeSpaces(output.join(' '));
}

/**
 * Drop a false start that the speaker (or a recogniser restart) immediately repeated
 * and then continued: "do you think you would have changed do you think you would have
 * changed your mind …" → "do you think you would have changed your mind …".
 */
function dropRestartedPrefix(text) {
  const tokens = normalizeSpaces(text).split(' ').filter(Boolean);
  if (tokens.length < 8) return normalizeSpaces(text);
  for (let start = 0; start < tokens.length - 7; start += 1) {
    const maxSize = Math.floor((tokens.length - start) / 2);
    for (let size = maxSize; size >= 4; size -= 1) {
      if (samePhrase(tokens, start, start + size, size)) {
        // Keep the later (continuing) copy; drop the abandoned first one.
        return normalizeSpaces(
          [...tokens.slice(0, start), ...tokens.slice(start + size)].join(' ')
        );
      }
    }
  }
  return normalizeSpaces(text);
}

function truncateAtWord(text, maxChars = MAX_CHAT_TEXT_CHARS) {
  const normalized = normalizeSpaces(text);
  if (normalized.length <= maxChars) return normalized;
  const slice = normalized.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(' ');
  return `${slice.slice(0, lastSpace > 120 ? lastSpace : maxChars).trim()}…`;
}

/**
 * Merge a newly recognised fragment into what we already have.
 * Handles the three ways SpeechRecognition overlaps itself:
 *   1. the new text extends the buffer   ("a b"      + "a b c"  → "a b c")
 *   2. the new text repeats the buffer   ("a b c"    + "a b"    → "a b c")
 *   3. head/tail overlap after a restart ("a b c"    + "b c d"  → "a b c d")
 * Anything genuinely new is appended.
 */
export function mergeTranscriptFragment(previous, incoming) {
  const prev = normalizeSpaces(previous);
  const next = normalizeSpaces(incoming);
  if (!next) return prev;
  if (!prev) return next;

  const prevLower = prev.toLowerCase();
  const nextLower = next.toLowerCase();

  // The buffer already contains this fragment (a restart re-sent old words).
  if (prevLower.includes(nextLower)) return prev;
  // The fragment contains everything we had — it's the fuller version.
  if (nextLower.includes(prevLower)) return next;

  // Largest overlap between the tail of the buffer and the head of the fragment.
  const maxOverlap = Math.min(prevLower.length, nextLower.length);
  for (let size = maxOverlap; size >= 4; size -= 1) {
    if (prevLower.slice(-size) === nextLower.slice(0, size)) {
      return normalizeSpaces(prev + next.slice(size));
    }
  }

  return `${prev} ${next}`;
}

/**
 * Final clean-up before a transcript is sent to the AI or saved.
 * Returns { text, changed, noisy } — `noisy` marks badly garbled speech input.
 */
export function sanitizeTranscriptForChat(text, { maxChars = MAX_CHAT_TEXT_CHARS } = {}) {
  const raw = normalizeSpaces(text).replace(WAKE_PREFIX_RE, '');
  if (!raw) return { text: '', changed: false, noisy: false };

  let cleaned = raw;
  for (let i = 0; i < 4; i += 1) {
    const next = dropRestartedPrefix(collapseRepeatedPhrases(cleaned));
    if (next === cleaned) break;
    cleaned = next;
  }

  const rawWords = raw.split(/\s+/).filter(Boolean).length;
  const cleanedWords = cleaned.split(/\s+/).filter(Boolean).length;
  const noisy = rawWords >= 28 && (
    cleanedWords / Math.max(rawWords, 1) < 0.68 ||
    raw.length > maxChars * 1.5
  );

  cleaned = truncateAtWord(cleaned, maxChars);
  return { text: cleaned, changed: cleaned !== raw, noisy };
}

/**
 * True when a recognised "user" transcript is really the avatar's own voice picked up
 * by the microphone (speaker echo). Without this the bot answers itself in a loop.
 *
 * Compares word overlap against what the avatar recently said: echo is a near-verbatim
 * copy, so a high overlap ratio is a reliable signal. Short replies are ignored to
 * avoid discarding genuine agreement like "yes" or "the spirit".
 */
export function looksLikeAvatarEcho(userText, avatarTexts, { minWords = 6, minRun = 6 } = {}) {
  const words = normalizeSpaces(userText).toLowerCase().split(' ').map(cleanToken).filter(Boolean);
  if (words.length < minWords) return false;

  const candidates = (Array.isArray(avatarTexts) ? avatarTexts : [avatarTexts])
    .map((t) => normalizeSpaces(t).toLowerCase().split(' ').map(cleanToken).filter(Boolean))
    .filter((a) => a.length);
  if (!candidates.length) return false;

  // Echo is verbatim, so look for the longest run of CONSECUTIVE matching words.
  // A bag-of-words overlap would flag genuine questions that merely reuse common
  // words ("when was the cannon first made"), so contiguity is what makes this safe.
  for (const candidate of candidates) {
    const prev = new Array(candidate.length + 1).fill(0);
    let longest = 0;
    for (let i = 1; i <= words.length; i += 1) {
      let diagonal = 0;
      for (let j = 1; j <= candidate.length; j += 1) {
        const temp = prev[j];
        if (words[i - 1] === candidate[j - 1]) {
          prev[j] = diagonal + 1;
          if (prev[j] > longest) longest = prev[j];
        } else {
          prev[j] = 0;
        }
        diagonal = temp;
      }
    }
    // Either a long verbatim run, or most of the utterance is a copy of the avatar.
    if (longest >= minRun || longest / words.length >= 0.7) return true;
  }
  return false;
}

/** Tidy a stored message for display (dashboard, PDF export). */
export function cleanMessageForDisplay(text, role = 'user') {
  if (role !== 'user') return truncateAtWord(text, MAX_DASHBOARD_TEXT_CHARS);
  return sanitizeTranscriptForChat(text, { maxChars: MAX_DASHBOARD_TEXT_CHARS }).text;
}
